import type { MyTrainingResponse } from "@lms/shared-types";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchJson } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { useBranding } from "../lib/branding";
import { useFormat } from "../lib/format";
import { inOrder, needsDoing } from "../lib/training";
import { Card, EmptyState, ErrorState, LoadingState, PageHeader, StatusPill } from "../components/ui";

/**
 * The top of Home (ACC-04, ui-design.md §8): the training that is waiting,
 * soonest first, with the late ones named as late. Three at most — Home is
 * "what do I do next", and the list is one click away for the rest.
 *
 * It fails quietly rather than loudly: a person whose training cannot be
 * loaded still has a usable Home, because the account card below it is not
 * what broke. The error says so and offers the list.
 */
function WhatNeedsDoing() {
  const { session, config } = useAuth();
  const { date } = useFormat();
  const [state, setState] = useState<{ data?: MyTrainingResponse; loading: boolean; error?: unknown }>({ loading: true });

  useEffect(() => {
    if (!session) return;
    let live = true;
    fetchJson<MyTrainingResponse>(config, session, "/v1/training")
      .then((data) => live && setState({ data, loading: false }))
      .catch((error: unknown) => live && setState({ loading: false, error }));
    return () => {
      live = false;
    };
  }, [config, session]);

  if (state.loading) return <LoadingState label="Loading what needs doing…" />;
  if (state.error !== undefined) return <ErrorState title="What needs doing could not be loaded." error={state.error} />;
  const all = state.data?.assignments ?? [];
  const waiting = inOrder(all.filter(needsDoing));
  const checking = all.filter((a) => a.status === "claimed").length;

  if (waiting.length === 0) {
    return (
      <EmptyState
        title={checking > 0 ? "Nothing to do right now" : "Nothing to do yet"}
        hint={checking > 0 ? `${checking === 1 ? "One course is" : `${checking} courses are`} waiting to be checked by your program director.` : "When a course is assigned to you it appears here, with what it is and when it is wanted."}
        action={all.length > 0 ? <Link to="/training">See all your training</Link> : undefined}
      />
    );
  }

  const overdue = waiting.filter((a) => a.overdue).length;
  return (
    <Card title="What needs doing">
      <p className="text-muted">
        {waiting.length === 1 ? "One course is" : `${waiting.length} courses are`} waiting for you
        {overdue > 0 ? <>, and {overdue === 1 ? "one is" : `${overdue} are`} already late</> : null}.
      </p>
      <ul className="mt-4 divide-y divide-line">
        {waiting.slice(0, 3).map((a) => (
          <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0">
            <div className="min-w-0">
              <p className="font-medium text-ink">
                <Link to={`/training/${a.id}`}>{a.item.title}</Link>
              </p>
              <p className="text-sm text-muted">
                {a.item.provider ? `${a.item.provider} · ` : ""}
                {a.dueAt ? (
                  <>
                    {a.overdue ? "was wanted by " : "wanted by "}
                    <time dateTime={a.dueAt}>{date(a.dueAt)}</time>
                  </>
                ) : (
                  "no date set"
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {a.overdue && <StatusPill tone="danger">Late</StatusPill>}
              <Link to={`/training/${a.id}`} className="inline-flex min-h-11 items-center">
                Start
              </Link>
            </div>
          </li>
        ))}
      </ul>
      {waiting.length > 3 && (
        <p className="mt-4 text-sm">
          <Link to="/training">See all {waiting.length} courses</Link>
        </p>
      )}
    </Card>
  );
}

/** The empty shell; the apprentice's Home (ACC-04) replaces it with Slice A. Signed in, it says who and where, and what roles they hold. */
export function HomePage() {
  const { status, me } = useAuth();
  const { productName } = useBranding();
  if (status === "signed-in" && me) {
    return (
      <>
        <PageHeader title="Home" subtitle={`Signed in to ${productName} as ${me.person.displayName}.`} />
        {/* What needs doing comes first, above the fold; the account card is reference (ui-design.md §8, the owner 2026-09-21). */}
        <div className="mb-6">
          <WhatNeedsDoing />
        </div>
        <Card title="Your account">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[auto_1fr]">
            <dt className="text-muted">Name</dt>
            <dd data-testid="me-name">{me.person.displayName}</dd>
            <dt className="text-muted">Email</dt>
            <dd>{me.person.email}</dd>
            <dt className="text-muted">Organization</dt>
            <dd>{me.tenant.displayName}</dd>
            {me.identityProvider && (
              <>
                <dt className="text-muted">Signs in through</dt>
                <dd>{me.identityProvider}</dd>
              </>
            )}
          </dl>
          <p className="mt-4">
            <Link to="/account">Account: profile, preferences, security, access</Link>
          </p>
        </Card>
      </>
    );
  }
  // While the session is being read or /v1/me is answering — up to half a minute on a cold cluster — a signed-in person
  // must not be told to sign in (walk 5, gairm/lms#203): the obvious click loops them back through the identity provider.
  if (status === "loading") {
    return (
      <>
        <PageHeader title="Home" subtitle={productName} />
        <LoadingState label="Loading your account…" />
      </>
    );
  }
  return (
    <>
      <PageHeader title="Home" subtitle={`${productName} is being built.`} />
      <EmptyState title="Nothing here yet" hint="Sign in to see your account." action={<Link to="/login">Sign in</Link>} />
    </>
  );
}
